import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { listDiagnosisReviewVisitsVisitIdDiagnosisReviewGet } from '../../services/api/sdk.gen';
import type { DiagnosisReviewRow } from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';

interface DiagnosisReviewProps {
  visitId: string;
}

/**
 * The episode's in-force diagnoses, shown read-only on a note that records none.
 *
 * Rows arrive in display order with their claim slot already assigned, so this
 * component neither sorts nor numbers — the printed chart reads the same list
 * from the same resolver.
 *
 * Deliberately not a trimmed-down `VisitActiveDiagnosis`: there is no search
 * box, no drag handle, no per-field editor and no save path, because the rows
 * belong to another visit. A note that could write here would own rows in
 * `visit_diagnoses`, and the billing worksheet takes its diagnosis codes from
 * whichever completed visit has them — so an editable discharge summary would
 * quietly outrank the assessment the codes came from.
 */
export const DiagnosisReview: React.FC<DiagnosisReviewProps> = ({
  visitId,
}) => {
  const [diagnoses, setDiagnoses] = useState<DiagnosisReviewRow[]>([]);
  const [sourceLabel, setSourceLabel] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await listDiagnosisReviewVisitsVisitIdDiagnosisReviewGet(
        {
          path: { visit_id: visitId },
          throwOnError: true,
        }
      );
      const data = response.data;
      setDiagnoses(data.diagnoses ?? []);
      setSourceLabel(data.source_label ?? null);
      setFailed(false);
    } catch (error) {
      console.error('Failed to load diagnosis review:', error);
      setFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading diagnoses...
      </div>
    );
  }

  if (failed) {
    return (
      <p className='text-sm text-muted-foreground'>
        Unable to load diagnoses. Please try again.
      </p>
    );
  }

  // The live endpoint only ever resolves against completed assessments, so an
  // empty list here means there is no finished assessment to read from -- not
  // that the episode has none. Saying the latter is a clinical claim.
  if (diagnoses.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        No completed assessment with diagnoses on this episode.
      </p>
    );
  }

  return (
    <div>
      {sourceLabel && (
        <p className='text-xs text-muted-foreground mb-2'>{sourceLabel}</p>
      )}
      {/* min-w keeps the six columns from collapsing into tall wrapped rows on
          a phone; the container scrolls instead. */}
      <div className='overflow-x-auto'>
        <table className='w-full min-w-[640px] text-sm'>
          <thead>
            <tr className='border-b border-border text-left text-xs text-muted-foreground'>
              {/* The UB-04 diagnosis locator, so this note can be read against
                  the claim. Server-assigned from the row order — see
                  claim_diagnosis_slots.py. */}
              <th className='py-2 pr-3 font-medium whitespace-nowrap'>
                Claim slot
              </th>
              <th className='py-2 pr-3 font-medium'>Code</th>
              <th className='py-2 pr-3 font-medium'>Description</th>
              <th className='py-2 pr-3 font-medium'>Severity</th>
              <th className='py-2 pr-3 font-medium'>Date</th>
              <th className='py-2 font-medium'>O/E</th>
            </tr>
          </thead>
          <tbody>
            {diagnoses.map(diagnosis => (
              <tr key={diagnosis.code} className='border-b border-border/50'>
                <td className='py-2 pr-3 font-mono whitespace-nowrap'>
                  {diagnosis.claim_slot ?? ''}
                </td>
                <td className='py-2 pr-3 font-mono whitespace-nowrap'>
                  {diagnosis.code}
                  {diagnosis.is_primary && (
                    <span className='ml-2 text-xs text-muted-foreground whitespace-nowrap'>
                      Primary
                    </span>
                  )}
                </td>
                <td className='py-2 pr-3'>{diagnosis.description}</td>
                <td className='py-2 pr-3'>{diagnosis.severity ?? ''}</td>
                <td className='py-2 pr-3 whitespace-nowrap'>
                  {diagnosis.onset_date
                    ? formatDateToMMDDYYYY(diagnosis.onset_date)
                    : ''}
                </td>
                <td className='py-2'>{diagnosis.oe ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DiagnosisReview;
